import { Link, useParams } from 'react-router'
import * as React from 'react'
import { ArrowLeft, Loader2, Lock, Presentation as PresentIcon, Printer } from 'lucide-react'
import type { ProposalDocumentDto } from '@lib/dto/proposal-document.dto'
import { parseUuid } from '@lib/validators/uuid.validator'
import { useProposalDocument } from '@/hooks/useProposalDocument'
import { ProposalDocument } from '@/proposal-document/ProposalDocument'
import { useChoosing } from '@/proposal-document/choosing'
import { Presentation, enterFullScreen } from '@/proposal-document/Presentation'
import { PdfButton } from '@/proposal-document/PdfButton'
import { proposalService } from '@/services/ProposalService'
import { Button } from '@/components/ui/button'

/**
 * The proposal as the client receives it, for staff.
 *
 * Outside `AppLayout`, like the map screens, and for a related reason: this is
 * the document itself, and app chrome around it would make it hard to tell what
 * the client will actually see. The only chrome is a thin bar that does not
 * print.
 *
 * There is no editor here and there is not meant to be. What a proposal says is
 * decided in `api/src/proposals/document/proposal-template.ts`; what it
 * contains comes from the survey. This page renders the result.
 *
 * It offers the client's choices too, because they are part of what the client
 * receives: the plans, the adjustments and the total that
 * follows. Without them this page would be a different document from the one
 * the client opens. The choices here are held in this page and go nowhere.
 */
export function ProposalDocumentPage() {
  const params = useParams<{ id: string; versionId: string }>()
  const proposalId = parseUuid(params.id)
  const versionId = parseUuid(params.versionId)
  const document = useProposalDocument(
    proposalId !== null && versionId !== null ? versionId : '',
  )

  if (proposalId === null || versionId === null) {
    return <DocumentUnavailable proposalId={proposalId} />
  }

  if (document.isPending) {
    return (
      <div className="proposal-stage flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-text" />
      </div>
    )
  }

  if (document.isError || document.data === undefined) {
    return <DocumentUnavailable proposalId={proposalId} />
  }

  return <StaffPreview proposalId={proposalId} document={document.data} />
}

function DocumentUnavailable({ proposalId }: { proposalId: string | null }) {
  return (
    <div className="proposal-stage flex items-center justify-center px-4">
      <div className="max-w-sm text-center">
        <p className="text-sm font-medium">This proposal could not be loaded</p>
        <p className="mt-1 text-sm text-muted-text">
          Check your connection and try again.
        </p>
        <Button asChild variant="outline" className="mt-4">
          <Link
            to={proposalId === null ? '/proposals' : `/proposals/${proposalId}`}
          >
            Back to the proposal
          </Link>
        </Button>
      </div>
    </div>
  )
}

function StaffPreview({
  proposalId,
  document,
}: {
  proposalId: string
  document: ProposalDocumentDto
}) {
  const choosing = useChoosing(document)
  const [presenting, setPresenting] = React.useState(false)

  return (
    <div className="proposal-stage">
      {/*
        `print:hidden` rather than a separate print route: the way this reaches
        a board is somebody pressing Cmd-P on this page, and a toolbar printed
        across the top of a client's proposal is the giveaway that it came out
        of somebody's CRM.
      */}
      <div className="proposal-topbar print:hidden">
        <Link to={`/proposals/${proposalId}`} className="proposal-topbar-back">
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          <span className="hidden sm:inline">Back to the proposal</span>
          <span className="sm:hidden">Back</span>
        </Link>
        <span className="proposal-topbar-actions">
          <span className="proposal-topbar-note">
            Preview: choices here are not saved
          </span>
          {document.isFrozen ? (
            /*
              Worth saying on the page. A sent proposal is a record of what
              was offered, not a view of the survey — editing a tree will not
              change it, and somebody who does not know that will wonder why.
            */
            <span
              className="proposal-topbar-note"
              title="This version has been sent, so it shows exactly what the client received"
            >
              <Lock className="h-3 w-3" aria-hidden />
              <span className="hidden sm:inline"> As sent</span>
            </span>
          ) : (
            <span className="proposal-topbar-note hidden sm:inline">
              Draft · revision {document.versionNumber}
            </span>
          )}
          <button
            type="button"
            className="proposal-topbar-button proposal-topbar-button--present"
            onClick={() => {
              enterFullScreen()
              setPresenting(true)
            }}
          >
            <PresentIcon className="h-3.5 w-3.5" aria-hidden />
            Present
          </button>
          {document.isFrozen ? (
            // Only a sent version: a PDF of a draft would be out of date
            // as soon as it was made, and look exactly like the real thing.
            <PdfButton load={() => proposalService.getPdf(document.versionId)} />
          ) : null}
          <button
            type="button"
            className="proposal-topbar-button"
            onClick={() => {
              window.print()
            }}
          >
            <Printer className="h-3.5 w-3.5" aria-hidden />
            Print
          </button>
        </span>
      </div>
      <ProposalDocument
        document={document}
        choosing={choosing}
        photoSource={{ kind: 'staff' }}
        markUnestimated
      />
      {presenting ? (
        <Presentation
          document={document}
          photoSource={{ kind: 'staff' }}
          shareUrl={null}
          onClose={() => {
            setPresenting(false)
          }}
        />
      ) : null}
    </div>
  )
}
